/**
 * Lines that know what was actually eaten. Toasts — ≤ 120 chars.
 *
 * Two mechanisms, both riding `foodlore.ts`'s deterministic taxonomy:
 *
 * 1. **Class overrides** — `log_success.<bucket>.<class>` sits one rung above
 *    the plain bucket in the engine's cascade, the same way a phase override
 *    does. Only the classes worth a distinct joke are written; everything
 *    `foodlore` recognises but this file ignores falls straight through to the
 *    ordinary pool, and a name it recognises not at all never reaches here.
 * 2. **Repeat callbacks** — `log_success.repeat_food`, fired by
 *    `REPEAT_FOOD_NOTABLE` at exactly the fourth and seventh logging of the
 *    same thing in a week. She notices a pattern twice and then lets you live.
 *
 * The truncation in `itemName` matters more here than anywhere else: a
 * catalogue name can be sixty characters, and these lines carry both the name
 * and a number.
 */

import type { SassContext } from '@gramas/shared'
import { fmtKcal, itemName, repeatNth, repeatNthAcc, repeatTimes } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const FOODS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  // ─── Sweets ───────────────────────────────────────────────────────────────
  'log_success.base.saldumynai': [
    () => 'Saldu. Skaičiams — ne.',
    () => 'Aha, cukrus. Užrašiau.',
    () => 'Desertas nekaltas. Kiekis — ne visada.',
    () => 'Saldumynai. Vienintelis dalykas, kurį visi įrašo pavėluotai.',
    () => 'Saldu dabar, aritmetika vakare.',
    () => 'Cukrus greitas. Aš greitesnė.',
    () => 'Malonumas trumpas, eilutė amžina.',
    () => 'Na gerai. Tai buvo desertas. Įrašiau.',
    () => 'Saldu ir sąžininga. Bent viena iš dviejų.',
    () => 'Cukrus nesislepia, kai kas nors rašo.',
    () => 'Sausainiukas. Žinoma.',
    () => 'Gerai jau. Visi kartais nusipelno.',
  ],
  'log_success.midnight.saldumynai': [
    () => 'Saldu po vidurnakčio. Klasika.',
    () => 'Naktis ir cukrus. Sena pora.',
    () => 'Naktinis desertas — tyliausias sprendimas dienoje.',
    () => 'Vidurnaktis, saldumynai, aš. Trys liudininkai.',
    () => 'Tamsoje saldumynai skanesni. Ir vis tiek skaičiuojasi.',
    () => 'Naktinis saldumynas. Rytas nieko nesakys. Aš sakau.',
    () => 'Aha. Cukrus trečią nakties. Suprantu.',
  ],

  // ─── Fried and fast ───────────────────────────────────────────────────────
  'log_success.base.kepta': [
    () => 'Aliejus irgi turi kalorijų. Deja.',
    () => 'Greitas maistas, lėtos pasekmės.',
    () => 'Kepta. Skanu. Brangu. Viskas kaip visada.',
    () => 'Keptuvė nieko nepamiršta. Aš irgi.',
    () => 'Traškumas kainuoja. Kaina — čia pat.',
    () => 'Greitas maistas, greitas įrašas. Simetriška.',
    () => 'Ne kiekvienas sprendimas turi būti sveikas. Bet visi būna įrašyti.',
    () => 'Kepta ir dokumentuota. Dviguba tradicija.',
    () => 'Na. Kvepėjo, spėju, nuostabiai.',
    () => 'Aha, kepta. Nieko naujo šiuose namuose.',
  ],
  'log_success.huge_portion.kepta': [
    (c) => `${fmtKcal(c.entryKcal)} kcal kepto. Monumentalu ir traškiai.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Aliejus dirbo viršvalandžius.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Greitas maistas, ilga byla.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal keptos drąsos.`,
    (c) => `Na po velnių. ${fmtKcal(c.entryKcal)} kcal — keptuvės triumfas.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal iš keptuvės. Kvėpuok.`,
  ],

  // ─── Drinks ───────────────────────────────────────────────────────────────
  'log_success.base.gerimai': [
    () => 'Skystos kalorijos pačios klastingiausios.',
    () => 'Gėrimas neprisotina, bet skaičiuojasi.',
    () => 'Skysta forma, kieti skaičiai.',
    () => 'Vienintelės kalorijos, kurių niekas neprisimena. Aš prisimenu.',
    () => 'Gerti — tas pats valgyti, tik greičiau.',
    () => 'Stiklinė tuščia, biudžetas — mažiau.',
    () => 'Aha. Tai irgi skaičiuojasi, jei tau įdomu.',
    () => 'Energetinis. Miegas atidėtas, palūkanos kaupiasi.',
  ],

  // ─── Vegetables ───────────────────────────────────────────────────────────
  'log_success.base.darzoves': [
    () => 'Įtartinai sąžininga.',
    () => 'Daržovės. Aš net nustebau.',
    () => 'Šitas įrašas atrodo gerai. Retenybė.',
    () => 'Sveikas sprendimas. Liudininkų netrūksta.',
    () => 'Nieko nesakysiu. Nes nėra ko.',
    () => 'Daržovės — tylus heroizmas.',
    () => 'Na va. Šito iš tavęs nesitikėjau.',
  ],

  // ─── Meat ─────────────────────────────────────────────────────────────────
  'log_success.base.mesa': [
    () => 'Mėsa. Baltymai atėjo, riebalai — nekviesti, bet irgi liko.',
    () => 'Vištienos krūtinėlė. Vėl. Bet niekas nesiskundžia baltymais.',
    () => 'Kiauliena keptuvėje. Kvapas jau pasiekė koridorių.',
    () => 'Faršas. Nežinau, kas iš jo išeis, bet skaičiuoju sąžiningai.',
    () => 'Cepelinai. Bulvė, apsimetanti dietiniu vakarienės pasirinkimu.',
    () => 'Kumpis ant duonos. Paprasta, greita, niekam nepavojinga.',
    () => 'Karbonadas. Keptuvė šįvakar laimėjo.',
    () => 'Mėsa vėl. Planas, ar tik įprotis?',
  ],

  // ─── Fish and seafood ───────────────────────────────────────────────────────
  'log_success.base.zuvis': [
    () => 'Žuvis. Omega-3 ir savigarba tuo pačiu metu.',
    () => 'Lašiša. Brangiau už jautieną, bet skamba kilniau.',
    () => 'Tunas iš skardinės. Paprasta, bet veikia.',
    () => 'Krevetės. Šventinis maistas eilinį trečiadienį.',
    () => 'Silkė. Senoviška, sąžininga, niekam nemeluoja.',
    () => 'Žuvis vėl. Ar tai omega-3, ar tiesiog patogu?',
  ],

  // ─── Dairy ────────────────────────────────────────────────────────────────
  'log_success.base.pieno': [
    () => 'Varškė. Baltymai be dramos.',
    () => 'Jogurtas be cukraus. Sąžiningas, bet vienišas skonis.',
    () => 'Sūris. Riebalai šypsosi, baltymai tyli.',
    () => 'Kefyras vakare. Skrandis padėkos rytoj.',
    () => 'Pienas puodelyje. Paprasčiausias dalykas šiame šaldytuve.',
    () => 'Varškė vėl. Šaukštas po šaukšto, be jokio malonumo veide.',
    () => 'Grietinė. Nedaug, bet sąžiningai užrašyta.',
    () => 'Pieno produktas. Kalcis džiaugiasi, aš — konstatuoju.',
  ],

  // ─── Health food and protein extras ────────────────────────────────────────
  'log_success.base.sveikuoliska': [
    () => 'Baltymų kokteilis. Mokslas stiklinėje, ne pyktis ant manęs.',
    () => 'Baltyminis batonėlis. Saldumynas, apsimetantis disciplina.',
    () => 'Avižos su baltymų milteliais. Pusryčiai, suplanuoti iš anksto.',
    () => 'Tofu. Drąsu. Skonio kol kas neradau, bet baltymai realūs.',
    () => 'Kinoja. Skamba rimtai, byloje atrodo dar rimčiau.',
    () => 'Chia sėklos. Mažos, bet turi nuomonę apie tavo pusryčius.',
    () => 'Dar vienas kokteilis. Šaukštelis mokslo, truputis vilties.',
    () => 'Sveikuoliškas pasirinkimas. Retas, bet užfiksuotas.',
  ],

  // ─── Grains and starches ────────────────────────────────────────────────────
  'log_success.base.kruopos': [
    () => 'Grikiai. Nuobodu, bet dietologė vertina nuoseklumą.',
    () => 'Makaronai. Angliavandeniai grįžo be perspėjimo.',
    () => 'Ryžiai. Balti, tylūs, visada tinkami.',
    () => 'Duona. Aštuonerius metus be jos. Dabar — grąžinta byla.',
    () => 'Bulvės. Ne keptos, tiesiog bulvės. Beveik nuobodu.',
    () => 'Grūdai lėkštėje. Energija ateičiai, ne malonumui.',
    () => 'Makaronai vėl. Skaičiuoju porciją, ne meilę jiems.',
    () => 'Grikiai su kažkuo. Sveika, bet nelabai įdomu rašyti apie tai.',
  ],

  // ─── The repeat callback ──────────────────────────────────────────────────
  'log_success.repeat_food': [
    (c) => `${repeatNth(c)} „${itemName(c)}“ per savaitę. Aš skaičiuoju.`,
    (c) => `Tas pats ${repeatNthAcc(c)} kartą: ${itemName(c)}. Ištikimybė ar inercija?`,
    (c) => `${itemName(c)} — ${repeatTimes(c)} per savaitę. Kulinarinė monogamija.`,
    (c) => `${repeatNth(c)} kartas. Šitas maistas jau turi savo skyrių.`,
    (c) => `Vėl ${itemName(c)}. ${repeatTimes(c)}. Neteisiu, dokumentuoju.`,
    (c) => `${repeatNth(c)} „${itemName(c)}“. Įprotis susiformavo oficialiai.`,
    (c) => `${itemName(c)}, ${repeatNthAcc(c)} kartą. Meniu siauras, byla stora.`,
    (c) => `${repeatTimes(c)} tas pats. Aš pradedu atpažinti tavo skonį.`,
    (c) => `Dar kartą ${itemName(c)}. Jau ${repeatTimes(c)}. Tradicija gimė.`,
    (c) => `${repeatNth(c)} kartas per savaitę. Nagi, pabandyk ką nors kita.`,
  ],
}

/** Expected draws per 14 days; the lint demands strictly more lines than this. */
export const FOODS_META: Readonly<Record<string, number>> = {
  'log_success.base.saldumynai': 8,
  'log_success.midnight.saldumynai': 3,
  'log_success.base.kepta': 6,
  'log_success.huge_portion.kepta': 3,
  'log_success.base.gerimai': 4,
  'log_success.base.darzoves': 4,
  'log_success.base.mesa': 4,
  'log_success.base.zuvis': 3,
  'log_success.base.pieno': 4,
  'log_success.base.sveikuoliska': 4,
  'log_success.base.kruopos': 4,
  'log_success.repeat_food': 4,
}
